/**
 * When the Tracker Order Queue row menu offers "Move to another location" and
 * "Split across locations".
 *
 * These are entry points only. Move opens the existing order detail sheet and
 * Split opens the existing resolve board; both call the existing procedures,
 * and the server re-checks everything. This predicate just keeps the menu from
 * offering an action the server is certain to refuse.
 */
import type { QueuedOrder } from '@/features/production-orders/types';

/**
 * The production statuses a move or split may start from.
 *
 * ⚠ KEEP IN LOCKSTEP with `RESOLVABLE_PRODUCTION_STATUSES` in
 * apps/backend/src/modules/production-orders/production-orders.types.ts. Every
 * in-production status is excluded there, because re-partitioning an order
 * whose garments are already moving through the floor is not safe.
 */
export const QUEUE_RELOCATABLE_STATUSES = ['NEW', 'ON_HOLD', 'WAITING_STOCK'] as const;

/** The slice of a queue row the predicate reads. */
export type RelocatableQueueOrder = Pick<QueuedOrder, 'organization_id' | 'prod_order'> &
  Partial<Pick<QueuedOrder, 'eligibility_status' | 'in_production_batch_id'>>;

export function canRelocateQueueOrder({
  canResolveLocations,
  callerOrgId,
  order,
}: {
  /** `can('orders.resolve_locations')`, the permission route and the resolver check. */
  canResolveLocations: boolean;
  callerOrgId: string | null | undefined;
  order: RelocatableQueueOrder;
}): boolean {
  if (!canResolveLocations) return false;

  // Own-org rows only. `route` and the split resolver hard-reject a row whose
  // org is not the caller's, for EVERY role including the floor owner's admin
  // (the move runs under the caller org's Shopify credentials). Offering the
  // action on a client's row would only ever produce an error.
  if (!callerOrgId || order.organization_id !== callerOrgId) return false;

  // Batched rows are refused by the resolver before anything else.
  if (order.prod_order?.batch_id) return false;

  // The parent row can still read NEW with no batch while its live garments
  // already sit in an open batch (a redo adopted by another floor moves only
  // the unit). Those garments are physical; routing must not move them.
  if (order.eligibility_status === 'IN_PRODUCTION' || order.in_production_batch_id) return false;

  // No prod_order on the row reads as NEW, as the table itself assumes.
  const status = order.prod_order?.production_status ?? 'NEW';
  return (QUEUE_RELOCATABLE_STATUSES as readonly string[]).includes(status);
}
